import type { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';

export const FIRST_PAGE = 1;

export function pageOffset(page: number, pageSize: number): number {
    const safePage = Math.max(FIRST_PAGE, Math.floor(page));
    return (safePage - FIRST_PAGE) * pageSize;
}

// For lists with no total (iTero orders): Next stays enabled while the shown page says more exist.
export function openEndedPageCount(page: number, hasMore: boolean): number {
    return hasMore ? page + 1 : page;
}

// The page belongs to one mode and filter set; changing either lands back on page 1.
export function pageScope(mode: string, filterKey: string): string {
    return `${mode}|${filterKey}`;
}

export function firstPageOnly<TPage, TParam>(
    data: InfiniteData<TPage, TParam> | undefined
): InfiniteData<TPage, TParam> | undefined {
    if (!data || data.pages.length <= 1) return data;
    return { pages: data.pages.slice(0, 1), pageParams: data.pageParams.slice(0, 1) };
}

export interface ScrollRowsMeta {
    isPending: boolean;
    isShowingPrevious: boolean;
    error: unknown;
    loadMoreError: unknown;
    hasMore: boolean;
    isLoadingMore: boolean;
    loadMore: () => void;
    retry: () => void;
}

// Shared by every infinite-scroll browse hook: this state derives only from the query's
// own flags, never from its page data, so it doesn't need to know what TData is.
export function scrollRowsMeta(query: UseInfiniteQueryResult<unknown, unknown>): ScrollRowsMeta {
    return {
        isPending: query.isPending,
        isShowingPrevious: query.isFetching && query.isPlaceholderData,
        // A failed next page keeps the rows already shown; only a first-page failure replaces them.
        error: query.isError && !query.isFetchNextPageError ? query.error : null,
        loadMoreError: query.isFetchNextPageError ? query.error : null,
        hasMore: Boolean(query.hasNextPage),
        isLoadingMore: query.isFetchingNextPage,
        loadMore: () => {
            if (query.hasNextPage && !query.isFetchingNextPage) void query.fetchNextPage();
        },
        retry: () => void query.refetch()
    };
}

// Pages can overlap when records change between requests; a repeated key would collide in React.
export function mergeUniquePages<TPage, TRow>(
    pages: readonly TPage[],
    rowsOf: (page: TPage) => readonly TRow[],
    keyOf: (row: TRow) => string
): TRow[] {
    const seen = new Set<string>();
    return pages
        .flatMap((page) => rowsOf(page))
        .filter((row) => {
            const key = keyOf(row);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
}
